// src/hooks/usePackets.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotesStore } from "../state/notes-store";
import { runMigrationsAndMirror } from "../persistence/migrations";
import {
  listPackets,
  createPacket,
  renamePacket,
  deletePacketById,
  setCurrentPacket,
  getCurrentPacketId,
} from "../persistence/packets";
import { exportCurrentPacketAsDocx } from "../services/packets-io";

export type PacketMeta = { id: string; name: string; updatedAt: string };

export function usePackets() {
  const { hydrate, setCurrentPacketId } = useNotesStore();
  const [packets, setPackets] = useState<PacketMeta[]>([]);
  const [currentPacketId, setCurrentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await runMigrationsAndMirror();
      const list = await listPackets();
      setPackets(list.map((p) => ({ id: p.id, name: p.name, updatedAt: p.updatedAt })));
      setCurrentId(await getCurrentPacketId());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // hydrate notes store, then mirror packets
    hydrate();
    void refresh();
  }, [hydrate, refresh]);

  const currentPacketName = useMemo(() => {
    if (!currentPacketId) return null;
    const p = packets.find((x) => x.id === currentPacketId);
    return p?.name ?? null;
  }, [currentPacketId, packets]);

  const createBlank = useCallback(async (name: string) => {
    const p = await createPacket(name, []);
    await setCurrentPacket(p.id);
    await setCurrentPacketId(p.id);
    await refresh();
    return p.id;
  }, [refresh, setCurrentPacketId]);

  const rename = useCallback(async (id: string, name: string) => {
    await renamePacket(id, name);
    await refresh();
  }, [refresh]);

  const remove = useCallback(async (id: string) => {
    await deletePacketById(id);
    await refresh();
  }, [refresh]);

  const selectAsCurrent = useCallback(async (id: string) => {
    await setCurrentPacket(id);
    await setCurrentPacketId(id);
    await refresh();
  }, [refresh, setCurrentPacketId]);

  const loadAndGoTag = useCallback(async (id: string) => {
    await setCurrentPacket(id);
    await setCurrentPacketId(id);
    await refresh();
    window.location.href = "/setup/tag";
  }, [refresh, setCurrentPacketId]);

  const exportDocx = useCallback(async (id: string) => {
    await exportCurrentPacketAsDocx(id);
  }, []);

  return {
    // state
    packets,
    currentPacketId,
    currentPacketName,
    loading,
    // actions
    refresh,
    createBlank,
    rename,
    remove,
    selectAsCurrent,
    loadAndGoTag,
    exportDocx,
  };
}
